import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Bell, MessageSquare, Clock, CheckCircle, AlertTriangle, Info, Eye } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/hooks/useNotifications';
import { useMessages } from '@/hooks/useMessages';
import { useReminders } from '@/hooks/useReminders';

interface InboxDropdownProps {
  type: 'notifications' | 'messages' | 'reminders';
  isOpen: boolean;
  onClose: () => void;
}

export const InboxDropdown: React.FC<InboxDropdownProps> = ({ type, isOpen, onClose }) => {
  const navigate = useNavigate();
  const notifications = useNotifications();
  const messages = useMessages();
  const reminders = useReminders();

  if (!isOpen) return null;

  const getIcon = (type: string, level?: string) => {
    switch (type) {
      case 'notifications':
        switch (level) {
          case 'error': return <AlertTriangle className="h-4 w-4 text-danger" />;
          case 'warning': return <AlertTriangle className="h-4 w-4 text-warning" />;
          case 'success': return <CheckCircle className="h-4 w-4 text-success" />;
          default: return <Info className="h-4 w-4 text-brand" />;
        }
      case 'messages':
        return <MessageSquare className="h-4 w-4 text-accent" />;
      case 'reminders':
        return <Clock className="h-4 w-4 text-warning" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const handleMarkAllAsRead = () => {
    switch (type) {
      case 'notifications':
        notifications.markAllAsRead();
        break;
      case 'messages':
        messages.markAsRead(messages.data.filter(m => !m.read_at).map(m => m.id));
        break;
      case 'reminders':
        // Reminders don't have a "read" state, they have status
        break;
    }
  };

  const handleItemClick = (item: any) => {
    // Mark as read if not already read
    switch (type) {
      case 'notifications':
        if (!item.read_at) {
          notifications.markAsRead([item.id]);
        }
        if (item.action_url) {
          navigate(item.action_url);
        }
        break;
      case 'messages':
        if (!item.read_at) {
          messages.markAsRead([item.id]);
        }
        navigate(`/inbox/messages/${item.id}`);
        break;
      case 'reminders':
        navigate(`/inbox/reminders/${item.id}`);
        break;
    }
    onClose();
  };

  const renderContent = () => {
    switch (type) {
      case 'notifications':
        return (
          <>
            <div className="flex items-center justify-between p-4 border-b border-border-subtle">
              <div className="flex items-center space-x-2">
                <Bell className="h-5 w-5 text-text-secondary" />
                <h3 className="font-semibold text-text-primary">Notifications</h3>
                {notifications.unreadCount > 0 && (
                  <Badge className="bg-badge-bg text-badge-text text-xs">
                    {notifications.unreadCount}
                  </Badge>
                )}
              </div>
              {notifications.unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-brand hover:text-brand-600"
                >
                  Mark all read
                </Button>
              )}
            </div>
            <ScrollArea className="max-h-80">
              <div className="p-2 space-y-1">
                {notifications.data.slice(0, 10).map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors hover:bg-hover-bg ${
                      !notification.read_at ? 'bg-chip-bg border-l-2 border-l-brand' : ''
                    }`}
                    onClick={() => handleItemClick(notification)}
                  >
                    <div className="flex items-start space-x-3">
                      {getIcon('notifications', notification.level)}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notification.read_at ? 'font-medium text-text-primary' : 'text-text-secondary'}`}>
                          {notification.title}
                        </p>
                        {notification.body && (
                          <p className="text-xs text-text-muted truncate">
                            {notification.body}
                          </p>
                        )}
                        <p className="text-xs text-text-muted mt-1">
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </p>
                      </div>
                      {!notification.read_at && (
                        <div className="w-2 h-2 bg-brand rounded-full flex-shrink-0 mt-1" />
                      )}
                    </div>
                  </div>
                ))}
                {notifications.data.length === 0 && (
                  <div className="text-center py-8 text-text-muted">
                    <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No notifications</p>
                  </div>
                )}
              </div>
            </ScrollArea>
            <div className="p-3 border-t border-border-subtle">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigate('/inbox/notifications');
                  onClose();
                }}
                className="w-full justify-center text-brand hover:text-brand-600"
              >
                <Eye className="h-4 w-4 mr-1" />
                View All Notifications
              </Button>
            </div>
          </>
        );

      case 'messages':
        return (
          <>
            <div className="flex items-center justify-between p-4 border-b border-border-subtle">
              <div className="flex items-center space-x-2">
                <MessageSquare className="h-5 w-5 text-text-secondary" />
                <h3 className="font-semibold text-text-primary">Messages</h3>
                {messages.unreadCount > 0 && (
                  <Badge className="bg-badge-bg text-badge-text text-xs">
                    {messages.unreadCount}
                  </Badge>
                )}
              </div>
              {messages.unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-brand hover:text-brand-600"
                >
                  Mark all read
                </Button>
              )}
            </div>
            <ScrollArea className="max-h-80">
              <div className="p-2 space-y-1">
                {messages.data.slice(0, 10).map((message) => (
                  <div
                    key={message.id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors hover:bg-hover-bg ${
                      !message.read_at ? 'bg-chip-bg border-l-2 border-l-brand' : ''
                    }`}
                    onClick={() => handleItemClick(message)}
                  >
                    <div className="flex items-start space-x-3">
                      {getIcon('messages')}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!message.read_at ? 'font-medium text-text-primary' : 'text-text-secondary'}`}>
                          {message.subject}
                        </p>
                        {message.snippet && (
                          <p className="text-xs text-text-muted truncate">
                            {message.snippet}
                          </p>
                        )}
                        <p className="text-xs text-text-muted mt-1">
                          {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                        </p>
                      </div>
                      {!message.read_at && (
                        <div className="w-2 h-2 bg-brand rounded-full flex-shrink-0 mt-1" />
                      )}
                    </div>
                  </div>
                ))}
                {messages.data.length === 0 && (
                  <div className="text-center py-8 text-text-muted">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No messages</p>
                  </div>
                )}
              </div>
            </ScrollArea>
            <div className="p-3 border-t border-border-subtle">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigate('/inbox/messages');
                  onClose();
                }}
                className="w-full justify-center text-brand hover:text-brand-600"
              >
                <Eye className="h-4 w-4 mr-1" />
                View All Messages
              </Button>
            </div>
          </>
        );

      case 'reminders':
        return (
          <>
            <div className="flex items-center justify-between p-4 border-b border-border-subtle">
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-text-secondary" />
                <h3 className="font-semibold text-text-primary">Reminders</h3>
                {reminders.overdueCount > 0 && (
                  <Badge className="bg-danger text-white text-xs">
                    {reminders.overdueCount} overdue
                  </Badge>
                )}
              </div>
            </div>
            <ScrollArea className="max-h-80">
              <div className="p-2 space-y-1">
                {reminders.data.slice(0, 10).map((reminder) => {
                  const isOverdue = reminder.due_at && new Date(reminder.due_at) < new Date();
                  return (
                    <div
                      key={reminder.id}
                      className={`p-3 rounded-lg cursor-pointer transition-colors hover:bg-hover-bg ${
                        isOverdue ? 'bg-danger/5 border-l-2 border-l-danger' : 'hover:bg-hover-bg'
                      }`}
                      onClick={() => handleItemClick(reminder)}
                    >
                      <div className="flex items-start space-x-3">
                        {getIcon('reminders')}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${isOverdue ? 'font-medium text-danger' : 'text-text-primary'}`}>
                            {reminder.title}
                          </p>
                          {reminder.description && (
                            <p className="text-xs text-text-muted truncate">
                              {reminder.description}
                            </p>
                          )}
                          {reminder.due_at && (
                            <p className={`text-xs mt-1 ${isOverdue ? 'text-danger' : 'text-text-muted'}`}>
                              Due {formatDistanceToNow(new Date(reminder.due_at), { addSuffix: true })}
                            </p>
                          )}
                        </div>
                        {isOverdue && (
                          <AlertTriangle className="h-4 w-4 text-danger flex-shrink-0 mt-1" />
                        )}
                      </div>
                    </div>
                  );
                })}
                {reminders.data.length === 0 && (
                  <div className="text-center py-8 text-text-muted">
                    <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No reminders</p>
                  </div>
                )}
              </div>
            </ScrollArea>
            <div className="p-3 border-t border-border-subtle">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigate('/inbox/reminders');
                  onClose();
                }}
                className="w-full justify-center text-brand hover:text-brand-600"
              >
                <Eye className="h-4 w-4 mr-1" />
                View All Reminders
              </Button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="absolute top-full right-0 mt-2 w-96 bg-bg-elevated border border-border-subtle rounded-lg shadow-elevation-2 z-50 animate-slideInFromTop">
      {renderContent()}
    </div>
  );
};